import * as React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { t } from 'ttag'
import { Card } from '../../components/Card/Card'
import Loader from '../../components/Loader/Loader'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import Typography from '@material-ui/core/Typography'
import { store } from '../../index'
import { completeSsoLogin } from '../Login/store/LoginActions'
import { WelcomeSidebar } from '../Welcome/Welcome'
import { push } from 'connected-react-router'
import { ROUTES } from '../../common/Routes/routePaths'
import '../../views/Login/Login.scss'

/**
 * SSO Callback page.
 *
 * After SAML authentication, the backend redirects here with either:
 * - a sessionId query parameter (success) → dispatches completeSsoLogin
 * - an error query parameter (failure) → redirects to /login with notification
 *
 * On success the saga exchanges sessionId, loads user data, and redirects.
 * On error (or missing sessionId) we redirect to /login immediately.
 */

const getErrorNotification = (error: string | null) => {
  console.log('SsoCallback error code:', error)
  switch (error) {
    case 'domainNotVerified':
      return {
        type: 'warning' as const,
        content: t`Your organization's domain has not been verified. Please contact your administrator.`,
      }
    case 'sso_config_not_found':
      return {
        type: 'warning' as const,
        content: t`SSO configuration not found. Please contact your administrator.`,
      }
    case 'sso_user_not_found':
      return {
        type: 'warning' as const,
        content: t`No account found for your email, and automatic provisioning is disabled. Please ask your administrator to create your account or enable provisioning.`,
      }
    case 'sso_no_email':
      return {
        type: 'warning' as const,
        content: t`Your SSO provider didn't return an email address. Please contact your administrator to fix the SAML attribute mapping.`,
      }
    case 'sso_auth_failed':
    case 'sso_failed':
      return {
        type: 'warning' as const,
        content: t`SSO authentication failed. Please try again or contact your administrator.`,
      }
    default:
      return {
        type: 'warning' as const,
        content: t`SSO login failed. Please try again. (${error || 'no code'})`,
      }
  }
}

export class SsoCallback extends React.Component<RouteComponentProps> {
  componentDidMount() {
    // Read from props.location.search (router state) rather than window.location.search.
    // On iOS we use createMemoryHistory, which doesn't update window.location, so
    // window.location.search would be empty after a router push().
    const search = this.props.location?.search || window.location.search
    const searchParams = new URLSearchParams(search)
    const sessionId = searchParams.get('sessionId')
    const error = searchParams.get('error')

    // If backend returned an error, redirect to login with notification
    if (error) {
      store.dispatch(push(ROUTES.LOGIN.to, { notification: getErrorNotification(error) }))
      return
    }

    // If no sessionId, redirect to login with generic error
    if (!sessionId) {
      store.dispatch(push(ROUTES.LOGIN.to, { notification: getErrorNotification(null) }))
      return
    }

    // Show the loader (Login.scss hides .loader by default, shows it when body has logging-in)
    document.body.classList.add('logging-in')

    // Dispatch the saga action to handle the full SSO login flow
    store.dispatch(completeSsoLogin({ sessionId }))
  }

  componentWillUnmount() {
    document.body.classList.remove('logging-in')
  }

  render() {
    return (
      <Card className="login pt-12">
        <div className="login__inner">
          <NotificationsWrapper />
          <Loader />
          <div className="login__form" style={{ textAlign: 'center' }}>
            <Typography className={'h1-desktop'} variant="h1" style={{ marginTop: 20 }}>
              {t`Signing you in...`}
            </Typography>
            <Typography variant="body2" style={{ marginTop: 10, color: '#666' }}>
              {t`Please wait while we complete your SSO login.`}
            </Typography>
          </div>
        </div>
        <WelcomeSidebar />
      </Card>
    )
  }
}
